import { $authHost } from "./index";
import { parseResponse } from "./utils";
import { provisionReportCRUD } from "./CRUD";
import {
  PROVISION_FALLBACK_SCHEMAS,
  PROVISION_LABORATORY_TYPES,
  PROVISION_BRANCHES,
  getCanonicalBranchFilialId,
  toApiDate,
} from "../models/provisionReport";

const BASE = "/api/v1/provision-report";

const FINANCE_LABELS = {
  budget: "Бюджет",
  extrabudget: "Внебюджет",
  paid: "Платные",
};

function mapFieldType(field = {}) {
  const key = field.key || field.Key || "";
  const label = field.label || field.Label || field.title || "";
  const type = (field.type || field.Type || "string").toLowerCase();

  // «Срок годности» — свободный текст (несколько дат через запятую), не календарь
  if (
    key === "expiry" ||
    /срок\s*годности/i.test(label)
  ) {
    return "text";
  }

  if (type === "date") return "date";
  if (type === "string" || type === "text") return "text";

  if (
    key === "balance" ||
    /остаток/i.test(label) ||
    type === "decimal"
  ) {
    return "decimal";
  }
  if (type === "computed") return "computed";
  if (key === "studies" || /кол-во\s*исследован/i.test(label)) {
    return "number";
  }
  return "number";
}

function fieldAggregate(field, mappedType) {
  if (mappedType === "decimal" || mappedType === "number") {
    const key = field.key || field.Key || "";
    if (key === "balance" || key === "planned" || mappedType === "decimal") {
      return "sum";
    }
  }
  return undefined;
}

/**
 * Контракт бэка columnGroups → дерево columns для грида.
 * Ключи листьев: groupKey.testKey.fieldKey / groupKey.noteKey
 */
export function mapColumnGroups(columnGroups = []) {
  return (columnGroups || []).map((group) => {
    const groupKey = group.key || group.Key;
    const tests = group.tests || group.Tests || [];
    const note = group.note || group.Note;

    const children = tests.map((test) => {
      const testKey = test.key || test.Key;
      const fields = test.fields || test.Fields || [];
      return {
        key: `${groupKey}.${testKey}`,
        title: test.label || test.Label || test.title || testKey,
        children: fields.map((field) => {
          const fieldKey = field.key || field.Key;
          const mappedType = mapFieldType(field);
          return {
            key: `${groupKey}.${testKey}.${fieldKey}`,
            title: field.label || field.Label || field.title || fieldKey,
            type: mappedType,
            aggregate: fieldAggregate(field, mappedType),
          };
        }),
      };
    });

    if (note) {
      const noteKey = note.key || note.Key || "note";
      children.push({
        key: `${groupKey}.${noteKey}`,
        title: note.label || note.Label || note.title || "Примечание",
        type: "text",
      });
    }

    return {
      key: groupKey,
      title: group.label || group.Label || group.title || groupKey,
      children,
    };
  });
}

function mapLegacyColumns(columns = []) {
  const walk = (col) => {
    const children = col.children || col.Children;
    const mapped = {
      key: col.key || col.Key,
      title: col.title || col.Title || col.label || col.Label,
      type: col.type || col.Type,
      aggregate: col.aggregate || col.Aggregate,
    };
    if (children?.length) {
      mapped.children = children.map(walk);
      delete mapped.type;
      delete mapped.aggregate;
    } else if (mapped.type) {
      mapped.type =
        mapped.type === "string"
          ? "text"
          : mapFieldType({ key: mapped.key, label: mapped.title, type: mapped.type });
      if (!mapped.aggregate) {
        mapped.aggregate = fieldAggregate(
          { key: String(mapped.key || "").split(".").pop() },
          mapped.type
        );
      }
    }
    return mapped;
  };
  return (columns || []).map(walk);
}

function mapFinanceSources(sources) {
  if (!sources?.length) return null;
  return sources.map((s) => {
    if (typeof s === "string") {
      return { key: s, title: FINANCE_LABELS[s] || s };
    }
    const key = s.key || s.Key || s;
    return {
      key,
      title:
        s.label ||
        s.Label ||
        s.title ||
        s.Title ||
        FINANCE_LABELS[key] ||
        key,
    };
  });
}

function mapBranches(branches) {
  if (!branches?.length) return null;
  return branches.map((b) => {
    const branchKey = b.key || b.Key || b.branchKey || b.BranchKey;
    const branchName = b.name || b.Name || b.branchName || b.BranchName;
    const filialId =
      b.filialId ??
      b.FilialId ??
      getCanonicalBranchFilialId(branchKey, branchName);
    const filialIds = b.filialIds ?? b.FilialIds ??
      (filialId == null ? undefined : Array.isArray(filialId) ? filialId : [filialId]);
    return {
      branchKey,
      branchName,
      filialId,
      filialIds,
    };
  });
}

const PROVISION_TEST_HEADERS = {
  coronavirus: "ПЦР - Коронавирусная инфекция",
  measles: "ИФА Корь IgM",
  enterovirus: "ПЦР - Энтеровирусная инфекция",
  cholera: "ПЦР - Холера",
  anthrax: "ПЦР - Сибирская язва",
};

function applyTestHeaderOverride(sectionKey, columns) {
  const header = PROVISION_TEST_HEADERS[sectionKey];
  if (!header) return columns;

  return (columns || []).map((column) => ({
    ...column,
    title:
      /^(budget|paid)\./.test(column.key || "") &&
      (column.key || "").endsWith(`.${sectionKey}`)
        ? header
        : column.title,
    children: column.children
      ? applyTestHeaderOverride(sectionKey, column.children)
      : column.children,
  }));
}

function getFallbackTab(laboratoryTypeId, sectionKey) {
  return PROVISION_FALLBACK_SCHEMAS[laboratoryTypeId]?.tabs?.find(
    (tab) => tab.sectionKey === sectionKey
  );
}

export const normalizeSchema = (payload, laboratoryTypeId) => {
  if (!payload) return null;
  const schema = payload.schema || payload.Schema || payload;
  const tabs = schema.tabs || schema.Tabs;
  if (!tabs?.length) return null;

  const schemaBranches = mapBranches(schema.branches || schema.Branches);

  return {
    laboratoryTypeId:
      schema.laboratoryTypeId ??
      schema.LaboratoryTypeId ??
      laboratoryTypeId,
    laboratoryName:
      schema.laboratoryName ||
      schema.LaboratoryName ||
      null,
    branches: schemaBranches,
    tabs: tabs.map((t) => {
      const columnGroups = t.columnGroups || t.ColumnGroups;
      const legacyColumns = t.columns || t.Columns;
      const tabBranches = mapBranches(t.branches || t.Branches);

      const financeSources =
        t.financeSources ?? t.FinanceSources ?? null;
      const fundingModes =
        mapFinanceSources(
          Array.isArray(financeSources) ? financeSources : null
        ) || mapFinanceSources(t.fundingModes || t.FundingModes);

      const sectionKey = t.sectionKey || t.SectionKey;
      const mappedColumns = columnGroups?.length
        ? mapColumnGroups(columnGroups)
        : mapLegacyColumns(legacyColumns || []);
      const isFluOrvi = sectionKey === "flu_orvi" || sectionKey === "influenza";
      const specializedFallbackTab = isFluOrvi
        ? getFallbackTab(PROVISION_LABORATORY_TYPES.VIROLOGICAL, "flu_orvi")
        : [
            "brucellosis",
            "tularemia",
            "cchf",
            "tbe",
            "lyme",
            "listeriosis",
          ].includes(sectionKey)
          ? getFallbackTab(PROVISION_LABORATORY_TYPES.OOI, sectionKey)
          : null;

      return {
        sectionKey,
        title: t.label || t.Label || t.title || t.Title,
        titleTemplate:
          specializedFallbackTab?.titleTemplate ||
          t.titleTemplate ||
          t.TitleTemplate,
        fundingModes: fundingModes?.length ? fundingModes : null,
        columns: applyTestHeaderOverride(
          sectionKey,
          specializedFallbackTab?.columns || mappedColumns
        ),
        branches: tabBranches || schemaBranches || null,
      };
    }),
  };
};

export function resolveActiveColumns(tabMeta, fundingMode) {
  if (!tabMeta?.columns?.length) return [];
  const columns = tabMeta.columns;
  if (!tabMeta.fundingModes?.length || !fundingMode) {
    return columns;
  }

  const matched = columns.filter((c) => c.key === fundingMode);
  if (matched.length) {
    return matched;
  }

  return prefixLeafKeys(columns, fundingMode);
}

function prefixLeafKeys(columns, prefix) {
  return (columns || []).map((col) => {
    if (col.children?.length) {
      return {
        ...col,
        children: prefixLeafKeys(col.children, prefix),
      };
    }
    return {
      ...col,
      key: `${prefix}.${col.key}`,
    };
  });
}

const normalizeReport = (payload) => {
  if (!payload) return null;
  const report =
    payload.result?.[0] ||
    (Array.isArray(payload.result) ? null : payload.result) ||
    payload;
  if (!report || typeof report !== "object") return null;

  const data = report.data ?? report.Data ?? { tabs: [] };
  return {
    id: report.id ?? report.Id ?? null,
    laboratoryTypeId: report.laboratoryTypeId ?? report.LaboratoryTypeId,
    reportDate: report.reportDate ?? report.ReportDate,
    data: {
      tabs: (data.tabs || data.Tabs || []).map((tab) => ({
        sectionKey: tab.sectionKey || tab.SectionKey,
        rows: (tab.rows || tab.Rows || []).map((row) => ({
          branchKey: row.branchKey || row.BranchKey,
          branchName: row.branchName || row.BranchName,
          filialId: row.filialId ?? row.FilialId,
          filialIds: row.filialIds ?? row.FilialIds,
          cells: row.cells || row.Cells || {},
        })),
      })),
    },
  };
};

function getBranchList(branches) {
  return branches?.length ? branches : PROVISION_BRANCHES;
}

const emptyRows = (branches) =>
  getBranchList(branches).map((b) => ({
    branchKey: b.branchKey,
    branchName: b.branchName,
    filialId:
      getCanonicalBranchFilialId(b.branchKey, b.branchName) ?? b.filialId,
    filialIds:
      b.filialIds ??
      (b.filialId == null
        ? getCanonicalBranchFilialId(b.branchKey, b.branchName)
        : Array.isArray(b.filialId)
        ? b.filialId
        : [b.filialId]),
    cells: {},
  }));

function toSavePayload(report) {
  const payload = {
    laboratoryTypeId: report.laboratoryTypeId,
    reportDate:
      typeof report.reportDate === "string"
        ? report.reportDate
        : toApiDate(report.reportDate),
    data: report.data,
  };
  if (report.id != null && report.id !== "") {
    payload.id = report.id;
  }
  return payload;
}

/**
 * Клиент к задеплоенным методам:
 * GET  /schema
 * GET  /current
 * GET/POST/PUT/DELETE /
 * POST /query
 */
export const provisionReportApi = {
  async getSchema(laboratoryTypeId) {
    try {
      const data = await parseResponse(
        $authHost.get(`${BASE}/schema`, {
          params: { laboratoryTypeId },
        })
      );
      const schema = normalizeSchema(data, laboratoryTypeId);
      if (schema?.tabs?.length) {
        return { schema, fromFallback: false };
      }
      console.warn("provision-report schema: empty tabs", data);
    } catch (e) {
      console.warn("provision-report schema fallback:", e);
    }
    const fallback = PROVISION_FALLBACK_SCHEMAS[Number(laboratoryTypeId)];
    return {
      schema: fallback || { laboratoryTypeId, tabs: [] },
      fromFallback: true,
    };
  },

  async getCurrent(laboratoryTypeId, reportDate) {
    const params = { laboratoryTypeId };
    if (reportDate) {
      params.reportDate =
        typeof reportDate === "string" ? reportDate : toApiDate(reportDate);
    }
    const data = await parseResponse(
      $authHost.get(`${BASE}/current`, { params })
    );
    const report = normalizeReport(data);
    if (!report) {
      throw new Error("Пустой ответ provision-report/current");
    }
    return report;
  },

  async getById(id) {
    const data = await provisionReportCRUD.get(id);
    return normalizeReport(data);
  },

  async query(body) {
    const data = await provisionReportCRUD.search(body);
    return {
      total: data.total ?? data.Total ?? 0,
      result: (data.result || data.Result || [])
        .map((item) => normalizeReport(item))
        .filter(Boolean),
    };
  },

  /** Есть id → PUT, нет → POST */
  async save(report) {
    const payload = toSavePayload(report);
    const data = payload.id
      ? await provisionReportCRUD.edit(payload)
      : await provisionReportCRUD.create(payload);
    return normalizeReport(data) || {
      ...payload,
      id: data?.id ?? data?.Id ?? payload.id ?? null,
    };
  },

  async remove(id) {
    return provisionReportCRUD.delete(id);
  },
};

export { provisionReportCRUD };

export function mergeBranchRows(rows = [], branches = null) {
  const list = getBranchList(branches);
  const byKey = {};
  (rows || []).forEach((r) => {
    const key = r.branchKey || r.BranchKey;
    if (!key) return;
    byKey[key] = {
      branchKey: key,
      branchName: r.branchName || r.BranchName,
      filialId: r.filialId ?? r.FilialId,
      filialIds: r.filialIds ?? r.FilialIds,
      cells: r.cells || r.Cells || {},
    };
  });
  return list.map((b) => {
    const existing = byKey[b.branchKey];
    const branchName = existing?.branchName || b.branchName;
    return {
      branchKey: b.branchKey,
      branchName,
      filialId:
        existing?.filialId ??
        b.filialId ??
        getCanonicalBranchFilialId(b.branchKey, branchName),
      filialIds:
        b.filialIds ??
        existing?.filialIds ??
        (b.filialId == null
          ? getCanonicalBranchFilialId(b.branchKey, branchName)
          : Array.isArray(b.filialId)
          ? b.filialId
          : [b.filialId]),
      cells: existing?.cells || {},
    };
  });
}

export function ensureTabInData(data, sectionKey, branches = null) {
  const tabs = [...(data?.tabs || [])];
  let tab = tabs.find((t) => t.sectionKey === sectionKey);

  if (!tab) {
    tab = {
      sectionKey,
      rows: emptyRows(branches),
    };
    tabs.push(tab);
  } else {
    tab = {
      ...tab,
      rows: mergeBranchRows(tab.rows, branches),
    };
    const idx = tabs.findIndex((t) => t.sectionKey === sectionKey);
    tabs[idx] = tab;
  }

  return { data: { tabs }, tab };
}
